import React, { Fragment, useCallback, useContext, useEffect, useState } from "react";
import { useAlert, useApi, useMount, useNewDownloads, useWebSocket } from "hooks";
import env from "environments/env";
import { AppContext, DownloadsContext, FeatureFlagContext, UserContext } from "contexts";
import { Route, Switch } from "react-router-dom";
import { Path, Portal, ReportStatus } from "enums";
import { REPORT_UPDATED_EVENT } from "utils";
import AppLoader from "components/commons/app-loader/app-loader";
import { Redirect } from "react-router-dom/cjs/react-router-dom.min";
import Header from "components/commons/header/header";
import Sidebar from "components/commons/sidebar/sidebar";
import { getUserV2 } from "apis/profile.api";
import PromosPage from "./promos.page";
import StationsPage from "./stations.page";
import ProductsPage from "./products.page";
import { getStationApi } from "apis";
import InventoryPage from "./inventory";
import RecordsPage from "./records";
import DownloadsPage from "./downloads.page";

const AuthorizedPage = () => {
  const { appState } = useContext(AppContext);
  const { setUserEmail } = useContext(FeatureFlagContext);
  const [user, setUser] = useState();
  const { RenderAlerts } = useAlert();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useWebSocket({ URL: env.WEB_SOCKET_URL });
  const downloads = useNewDownloads({ userId: user?.userId });
  const { completeDownload, discardPendingDownload } = downloads;

  // count a finished export as soon as the websocket reports it
  useEffect(() => {
    const onReportUpdated = ({ detail }) => {
      if (detail?.status === ReportStatus.Success) {
        completeDownload(detail.fileName, detail.reportId);
      } else if (detail?.status === ReportStatus.Failed) {
        discardPendingDownload(detail.fileName);
      }
    };
    window.addEventListener(REPORT_UPDATED_EVENT, onReportUpdated);
    return () => window.removeEventListener(REPORT_UPDATED_EVENT, onReportUpdated);
  }, [completeDownload, discardPendingDownload]);

  const { request: getUserRequest, loading: fetchingUser } = useApi({
    api: getUserV2,
    pageError: true,
  });

  const { request: getStationByStationId } = useApi({
    api: getStationApi,
    pageError: false,
    modalError: false,
    handleOwnError: true,
  });

  useMount(() => {
    fetchUser();
  });

  const fetchUser = useCallback(async () => {
    const res = await getUserRequest();

    const getStationIds = (res) => {
      const accessRoles = res?.userAccesses
        ?.filter((element) => element.portal === Portal.HOS)
        .map((roleAccess) => (!isNaN(roleAccess?.stationId) ? roleAccess?.stationId : null));

      return accessRoles.filter((stationId) => !!stationId);
    };

    const stationIds = getStationIds(res);
    let stations = [];
    if (stationIds?.length > 0) {
      try {
        stations = await Promise.all(
          stationIds.map((stationId) => getStationByStationId({ stationId: stationId })),
        );
      } catch (error) {}
    }

    const userAccesses = res.userAccesses?.map((roleAccess) => ({
      ...roleAccess,
      station: stations?.find(({ stationId }) => stationId === roleAccess?.stationId) || null,
    }));

    setUser({
      ...res,
      userAccesses,
    });
    setUserEmail(res.email);
  }, [getStationByStationId, getUserRequest, setUserEmail]);

  const loading = fetchingUser || appState?.loading;
  return (
    <UserContext.Provider value={{ user, fetchUser, loading: fetchingUser }}>
      <DownloadsContext.Provider value={downloads}>
        {loading ? (
          <AppLoader />
        ) : user ? (
          <Fragment>
            <Header onClick={() => setIsDrawerOpen(!isDrawerOpen)} />
            <Sidebar />

            <div
              style={{
                marginLeft: "196px",
                marginTop: "60px",
                padding: "27px 36px",
                minWidth: "1170px",
                overflowX: "auto",
                minHeight: "100vh",
                backgroundColor: "#fff",
              }}
            >
              <div
                style={{
                  overflowX: "auto",
                  padding: "27px 36px",
                }}
              >
                <RenderAlerts />
                <Switch>
                  <Route path={Path.PromosList} component={PromosPage} />
                  <Route path={Path.Stations} component={StationsPage} />
                  <Route path={Path.Products} component={ProductsPage} />
                  <Route path={Path.Inventory} component={InventoryPage} />
                  <Route path={Path.Records} component={RecordsPage} />
                  <Route path={Path.Downloads} component={DownloadsPage} />
                  <Redirect to={Path.Stations} />
                </Switch>
              </div>
            </div>
          </Fragment>
        ) : null}
      </DownloadsContext.Provider>
    </UserContext.Provider>
  );
};

export default AuthorizedPage;
